/**
 * ============================================================
 * AdminProjectForm.js — Create/Edit Project (Admin Panel)
 * ============================================================
 *
 * Modes:
 *   - NEW:  /admin/projects/new
 *   - EDIT: /admin/projects/:id
 *
 * Loads:
 *   - Project data (GET /api/projects/:id)
 *   - All people (GET /api/people)
 *   - All publications (GET /api/publications)
 *
 * Saves:
 *   - Create project (POST /api/projects)
 *   - Update project (PUT /api/projects/:id)
 *   - Delete project (DELETE /api/projects/:id)
 *
 * Notes:
 *   - Uses authFetch() for auto-redirect on token expiry
 **/

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";

import SynlabAdminNavbar from "../components/SynlabAdminNavbar";
import SynlabAdminFooter from "../components/SynlabAdminFooter";
import ItemList from "../components/ListItem";

import { authFetch } from "../../utils/authFetch";

import "./css/AdminSubpage.css";
import "./css/AdminForm.css";

import { projectPhoto } from "../../utils/mediaPaths";

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------
function AdminProjectForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const editing = id && id !== "new";

  // Project state
  const [project, setProject] = useState({
    name: "",
    shortname: "",
    startyear: "",
    endyear: 0,
    description: "",
    url: "",
    video: "",
    past: 0,
    photo: "",
    people: [],
    publications: [],
  });

  // Lookups
  const [file, setFile] = useState(null);

  // Lookups
  const [allPeople, setAllPeople] = useState([]);
  const [allPublications, setAllPublications] = useState([]);

  // Search inputs for add lists
  const [searchPeople, setSearchPeople] = useState("");
  const [searchPublications, setSearchPublications] = useState("");

  // Delete modal
  const [showDelete, setShowDelete] = useState(false);

  // ------------------------------------------------------------
  // Validate token on initial page load
  // ------------------------------------------------------------
  useEffect(() => {
    async function validateToken() {
      try {
        await authFetch("/api/authenticate");
        // If token is valid, nothing happens
      } catch (err) {
        // authFetch already handles redirect for 401/403
        console.error("Token validation failed:", err);
      }
    }
    validateToken();
  }, []);

  // ------------------------------------------------------------
  // Utility: always return an array
  // ------------------------------------------------------------
  function safeArray(value) {
    return Array.isArray(value) ? value : [];
  }

  // ------------------------------------------------------------
  // Filtered people/pubs to add (avoid duplicates)
  // ------------------------------------------------------------

  // Filtered list of people
  const filteredPeople = useMemo(() => {
    const s = searchPeople.toLowerCase().trim();
    if (!s) return [];

    const matches = allPeople.filter(
      (p) =>
        p.firstname.toLowerCase().includes(s) ||
        p.lastname.toLowerCase().includes(s)
    );

    return matches.filter(
      (p) => !project.people.some((x) => x.id === p.id)
    );
  }, [searchPeople, allPeople, project.people]);

  // Filtered list of publications
  const filteredPublications = useMemo(() => {
    const s = searchPublications.toLowerCase().trim();
    if (!s) return [];

    const matches = allPublications.filter((pub) =>
      (pub.title || "").toLowerCase().includes(s)
    );

    return matches.filter(
      (pub) => !project.publications.some((x) => x.id === pub.id)
    );
  }, [searchPublications, allPublications, project.publications]);

  // ------------------------------------------------------------
  // Loaders (project data + lookups)
  // ------------------------------------------------------------
  async function loadProject(editing, id, navigate, setProject) {
    // Load project data only if editing an existing project
    if (!editing) return;

    try {
      const res = await authFetch(`/api/projects/${id}`);
      if (!res) return navigate("/admin/projects");

      const data = await res.json();

      const projObj   = data.projectData || {};
      const peopleArr = safeArray(data.personData);
      const pubArr    = safeArray(data.publicationData);

      setProject({
        ...projObj,
        people: peopleArr.map((p) => ({
          ...p,
          id: p.id,
          position: p.person_position ?? 0,
          faculty: !!p.person_faculty,
          alumni: !!p.person_alumni,
        })),
        publications: pubArr.map((pub) => ({
          ...pub,
          position: pub.position ?? 0,   // add this if your data supports positions
        })),
      });

    } catch (err) {
      console.error("Failed to load project:", err);
      navigate("/admin/projects");
    }
  }

  async function loadPeople(setAllPeople) {
    // Load people data as an array
    try {
      const res = await authFetch("/api/people/all");
      if (!res) return setAllPeople([]);

      const data = await res.json();
      setAllPeople(safeArray(data));
    } catch {
      setAllPeople([]);
    }
  }

  async function loadPublications(setAllPublications) {
    // Load publications data as an array
    try {
      const res = await authFetch("/api/publications");
      if (!res) return setAllPublications([]);

      const data = await res.json();
      setAllPublications(safeArray(data));
    } catch {
      setAllPublications([]);
    }
  }

  // ------------------------------------------------------------
  // Load all data
  // ------------------------------------------------------------
  useEffect(() => {
    async function loadData() {
      try {
        // Validate auth
        await authFetch("/api/authenticate");

        // Parallel-load all lookups
        await Promise.all([
          loadPeople(setAllPeople),
          loadPublications(setAllPublications),
        ]);

        // Load project only after lookups are ready
        await loadProject(editing, id, navigate, setProject);

      } catch (err) {
        console.error("AdminProjectsForm load error:", err);
        if (editing) navigate("/admin/projects");
      }
    }

    loadData();
  }, [editing, id, navigate]);

  // ------------------------------------------------------------
  // Helpers
  // ------------------------------------------------------------
  function updateField(field, value) {
    setProject((prev) => ({ ...prev, [field]: value }));
  }

  function handleInputChange(e) {
    const { name, value, type, checked, files } = e.target;

    if (name === "photo") {
      if (files && files[0]) setFile(files[0]);
      return;
    }

    if (type === "checkbox") {
      updateField(name, checked ? 1 : 0);
      return;
    }

    updateField(name, value);
  }

  // ---- Remove project photo ----
  function removePhoto() {
    setProject((prev) => ({ ...prev, photo: "" }));
    setFile(null);
  }

  // ---- Add / remove people ----
  function addPerson(person) {
    setProject((prev) => ({
      ...prev,
      people: prev.people.concat({
        ...person,
        position: 0,
        faculty: false,
        alumni: false,
      }),
    }));
  }

  function removePerson(personId) {
    setProject((prev) => ({
      ...prev,
      people: prev.people.filter((p) => p.id !== personId),
    }));
  }

  function updatePerson(personId, patch) {
    setProject((prev) => ({
      ...prev,
      people: prev.people.map((p) =>
        p.id === personId ? { ...p, ...patch } : p
      ),
    }));
  }

  // ---- Add / remove publications ----
  function addPublication(pub) {
    setProject((prev) => ({
      ...prev,
      publications: prev.publications.concat({
        ...pub,
      }),
    }));
  }

  function removePublication(pubId) {
    setProject((prev) => ({
      ...prev,
      publications: prev.publications.filter((p) => p.id !== pubId),
    }));
  }

  function updatePublication(pubId, patch) {
    setProject((prev) => ({
      ...prev,
      publications: prev.publications.map((p) =>
        p.id === pubId ? { ...p, ...patch } : p
      ),
    }));
  }

  // ------------------------------------------------------------
  // Submit (create/update)
  // ------------------------------------------------------------
  async function handleSubmit(e) {
    e.preventDefault();

    try {
      const formData = new FormData();

      // photo: new file wins, else existing
      if (file) formData.append("photo", file);
      else formData.append("photo", project.photo || "");

      const fields = [
        "name", "shortname", "startyear", "endyear",
        "description", "url", "video", "past"
      ];

      fields.forEach((f) => formData.append(f, project[f] ?? ""));

      formData.append("people", JSON.stringify(project.people || []));
      formData.append("publications", JSON.stringify(project.publications || []));

      const endpoint = editing
        ? `/api/projects/${id}`
        : `/api/projects`;

      const method = editing ? "PUT" : "POST";

      const res = await authFetch(endpoint, {
        method,
        body: formData,
      });

      if (!res) return;
      navigate("/admin/projects");

    } catch (err) {
      console.error("Save failed:", err);
      alert("Failed to save project.");
    }
  }

  // ------------------------------------------------------------
  // Delete project
  // ------------------------------------------------------------
  async function handleDelete() {
    try {
      const res = await authFetch(`/api/projects/${id}`, {
        method: "DELETE",
      });

      if (!res) return;

      setShowDelete(false);
      navigate("/admin/projects");
    } catch (err) {
      console.error("Delete error:", err);
      alert("Failed to delete project.");
    }
  }

  // ----------------------------------------------------------
  // Get the photo path for display
  // ----------------------------------------------------------
  const photoSrc = projectPhoto(project.photo);

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <div className="admin-wrapper">

      <SynlabAdminNavbar />

      <div className="admin-subpage-container">

        <div className="admin-page-header">
          <h1 className="admin-page-title">
            {editing ? "Edit Project" : "Create Project"}
          </h1>
        </div>

        <p className="admin-page-description">
          {editing
            ? "Update project details, image, people, and publications."
            : "Create a new project for the Synlab site."}
        </p>

        <form onSubmit={handleSubmit} className="admin-form">

          {/* ----- BASIC INFO ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Basic Info</h2>

            <div className="admin-form-grid">

              {/* ----- PROJECT NAME ----- */}
              <div className="admin-form-field">
                <label className="form-label">Project Name</label>
                <input
                  name="name"
                  className="form-control"
                  value={project.name}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* ----- SHORT NAME ----- */}
              <div className="admin-form-field">
                <label className="form-label">Short Name</label>
                <input
                  name="shortname"
                  className="form-control"
                  value={project.shortname}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- START YEAR ----- */}
              <div className="admin-form-field">
                <label className="form-label">Start Year</label>
                <input
                  name="startyear"
                  className="form-control"
                  value={project.startyear}
                  onChange={handleInputChange}
                  required
                />
              </div>

              {/* ----- END YEAR ----- */}
              <div className="admin-form-field">
                <label className="form-label">End Year (0 = current)</label>
                <input
                  name="endyear"
                  className="form-control"
                  value={project.endyear ?? 0}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- DESCRIPTION ----- */}
              <div className="admin-form-field admin-form-field-wide">
                <label className="form-label">Description</label>
                <textarea
                  name="description"
                  className="form-control"
                  rows={8}
                  value={project.description}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- PROJECT PAGE URL ----- */}
              <div className="admin-form-field">
                <label className="form-label">Project Page URL</label>
                <input
                  name="url"
                  className="form-control"
                  value={project.url}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- VIDEO EMBED URL ----- */}
              <div className="admin-form-field">
                <label className="form-label">Video Embed URL</label>
                <input
                  name="video"
                  className="form-control"
                  value={project.video}
                  onChange={handleInputChange}
                />
              </div>

              {/* ----- PAST PROJECT CHECKBOX ----- */}
              <div className="admin-form-field admin-checkbox-field admin-form-field-wide">
                <label className="form-label">Past Project</label>
                <div className="admin-checkbox-inline">
                  <input
                    name="past"
                    type="checkbox"
                    checked={!!project.past}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ----- PHOTO ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Project Image</h2>

            {project.photo && !file ? (
              <div className="admin-photo-row">
                <img
                  src={photoSrc}
                  alt="project"
                  className="admin-photo-preview"
                />
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={removePhoto}
                >
                  Remove Photo
                </button>
              </div>
            ) : (
              <div className="admin-form-field">
                <label className="form-label">Upload Photo</label>
                <input
                  name="photo"
                  type="file"
                  accept="image/*"
                  className="form-control"
                  onChange={handleInputChange}
                />
              </div>
            )}
          </div>

          {/* ----- PEOPLE ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">People</h2>

            <div className="admin-form-field admin-form-field-wide">
              <label className="form-label">Search people to add</label>
              <input
                className="form-control"
                value={searchPeople}
                onChange={(e) => setSearchPeople(e.target.value)}
                placeholder="Search by name..."
              />
            </div>

          {/* ----- ADDABLE LIST ----- */}
            <div className="admin-add-list">
              {safeArray(filteredPeople).length === 0 ? (
                <div className="admin-empty-list">No people found</div>
              ) : (
                safeArray(filteredPeople).map((person) => (
                  <ItemList
                    key={person.id}
                    name={`${person.firstname} ${person.lastname}`}
                    isAdd
                    backgroundColor="#f0f0f0"
                    onClick={() => addPerson(person)}
                  />
                ))
              )}
            </div>

            {/* ----- SELECTED PEOPLE ----- */}
            <div className="admin-selected-list">
              {safeArray(project.people).map((p) => (
                <ItemList
                  key={p.id}
                  name={`${p.firstname} ${p.lastname}`}
                  isDelete
                  onClick={() => removePerson(p.id)}
                  edit={
                    <div className="admin-inline-edit">
                      <label>Position</label>
                      <input
                        className="form-control form-control-sm"
                        value={p.position}
                        onChange={(e) =>
                          updatePerson(p.id, { position: e.target.value })
                        }
                      />

                      <label>Faculty</label>
                      <input
                        type="checkbox"
                        checked={!!p.faculty}
                        onChange={(e) =>
                          updatePerson(p.id, { faculty: e.target.checked })
                        }
                      />
                    </div>
                  }
                />
              ))}
            </div>
          </div>

          {/* ----- PUBLICATIONS ----- */}
          <div className="admin-form-section">
            <h2 className="admin-section-title">Publications</h2>

            <div className="admin-form-field admin-form-field-wide">
              <label className="form-label">Search publications to add</label>
              <input
                className="form-control"
                value={searchPublications}
                onChange={(e) => setSearchPublications(e.target.value)}
                placeholder="Search by title..."
              />
            </div>

            {/* ----- ADDABLE LIST ----- */}
            <div className="admin-add-list">
              {safeArray(filteredPublications).length === 0 ? (
                <div className="admin-empty-list">
                  No publications found
                </div>
              ) : (
                safeArray(filteredPublications).map((pub) => (
                  <ItemList
                    key={pub.id}
                    name={pub.title}
                    isAdd
                    backgroundColor="#f0f0f0"
                    onClick={() => addPublication(pub)}
                  />
                ))
              )}
            </div>

            {/* ----- SELECTED PUBLICATIONS ----- */}
            <div className="admin-selected-list">
              {safeArray(project.publications).map((pub) => (
                <ItemList
                  key={pub.id}
                  name={pub.title}
                  isDelete
                  onClick={() => removePublication(pub.id)}
                />
              ))}
            </div>
          </div>

          {/* ----- BUTTONS ----- */}
          <div className="admin-form-buttons">
            <button className="btn btn-primary" type="submit">
              {editing ? "Save Changes" : "Create Project"}
            </button>

            {editing && (
              <button
                className="btn btn-danger"
                type="button"
                onClick={() => setShowDelete(true)}
              >
                Delete Project
              </button>
            )}

            <button
              className="btn btn-secondary"
              type="button"
              onClick={() => navigate("/admin/projects")}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* ----- DELETE CONFIRM MODAL ----- */}
      <Modal show={showDelete} onHide={() => setShowDelete(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Delete Project</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Deleting this project cannot be undone. Continue?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
          <Button variant="secondary" onClick={() => setShowDelete(false)}>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <SynlabAdminFooter />
    </div>
  );
}

export default AdminProjectForm;
